import { Block, Delete, Print } from "@mui/icons-material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Chip, Collapse, Icon, IconButton, List, ListItem, ListItemText, Menu, MenuItem, MenuList, Skeleton, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import CancelaDFeDialog from "pages/Venda/dialogs/CancelaDFeDialog";
import { AssociacaoByStatus, StatusNotaFiscal } from "pages/Venda/vendas.model";
import { useState } from "react";

import { EntradaSaida, EntradaSaidaDescritivo } from "utils/types";
import { ColumnData, currencyOf, deepCopy, resolveWrapper } from "utils/util";
import { FlowConsignadoItemVisualizarDTO, FlowConsignadoVisualizarNotaBaseDTO } from "../movimentacoes/movimentacoes.model";
import MiniNota from "./MiniNota";
import { theme } from "theme";

export enum TiposNotasTabView {
    SAIDA = 'saida',
    ENTRADA = 'entrada',
    TODOS = 'todos',
}

export enum ViewMode {
    GRID = 'grid',
    LIST = 'list'
}

const VisualizadorNotas = (props: {
    loading?: boolean,
    tab: TiposNotasTabView,
    initialViewMode?: ViewMode;
    data: FlowConsignadoVisualizarNotaBaseDTO[];
    onClickMiniNota?: (itens: FlowConsignadoItemVisualizarDTO[]) => void;
    onDeleteNota?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onImprimir?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onImprimirDanfe?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onReload: () => void;
    toogleButtonPropsTop?: string;
}) => {

    const [localState, setLocalState] = useState<{
        selectedItens: { [chaveAcesso: string]: boolean; };
        viewMode: ViewMode;
        shouldShowCancelarNotaDialog: boolean;
        selectedItem: FlowConsignadoVisualizarNotaBaseDTO | null,
    }>({
        selectedItens: {},
        viewMode: props.initialViewMode ?? ViewMode.GRID,
        shouldShowCancelarNotaDialog: false,
        selectedItem: null,
    });

    const _columns: ColumnData<FlowConsignadoVisualizarNotaBaseDTO & { __toggle: unknown; __acoes: unknown; }>[] = [
        {
            header: '',
            id: 'toggle',
            key: '__toggle',
            type: 'custom',
            width: 10,
            convert: item => <IconButton
                size="small"
                onClick={() => setLocalState(prev => {
                    const key = item.chaveAcesso;
                    const { selectedItens } = deepCopy(prev);
                    if (!selectedItens[key]) {
                        selectedItens[key] = true;
                    } else {
                        delete selectedItens[key];
                    }
                    return {
                        ...prev,
                        selectedItens,
                    };
                })}
            >
                {!!localState.selectedItens[item.chaveAcesso] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
        },

        {
            header: 'Nota',
            id: 'serieNumero',
            key: 'serieNumero',
            type: 'string',
            width: 10,
        },

        {
            header: 'Natureza da operação',
            id: 'naturezaOperacao',
            key: 'naturezaOperacao',
            type: 'string',
            width: 10,
        },

        {
            header: 'Emissão',
            id: 'emissao',
            key: 'emissao',
            type: 'string',
            textAlign: 'center',
            width: 10,
        },

        {
            header: 'Situação',
            id: 'situacao',
            key: 'situacao',
            type: 'string',
            textAlign: 'center',
            width: 10,
            convert: item => {
                const corObj = AssociacaoByStatus[item.situacao];
                return <Chip
                    label={item.situacao}
                    size="small"
                    sx={{
                        color: theme.palette.background.default,
                        background: corObj.rgb2 ?? corObj.rgb,
                    }}
                />;
            },
        },

        {
            header: 'Tipo',
            id: 'tipo',
            key: 'tipo',
            type: 'string',
            textAlign: 'center',
            width: 10,
            convert: item => {
                return <Chip
                    label={EntradaSaidaDescritivo[item.tipo]}
                    size="small"
                    sx={{
                        color: theme.palette.background.default,
                        background: item.tipo === 'SAIDA' ? theme.palette.success.main : theme.palette.error.main
                    }}
                />;
            },
        },

        {
            header: '',
            id: 'acoes',
            key: '__acoes',
            type: 'custom',
            textAlign: 'center',
            width: 10,
            visible: () => !!props.onDeleteNota || !!props.onImprimir || !!props.onImprimirDanfe,
            convert: item => <ImprimirButton
                item={item}
                onDeleteNota={props.onDeleteNota}
                onImprimir={props.onImprimir}
                onImprimirDanfe={props.onImprimirDanfe}
                onCancelar={item => {
                    setLocalState(prev => ({
                        ...prev,
                        shouldShowCancelarNotaDialog: true,
                        selectedItem: item,
                    }));
                }}
            />
        },
    ];

    const columns = _columns.filter(s => !s.visible || s.visible());

    if (props.loading) {
        return <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: '20px',
        }}>
            {Array.from({ length: 12 }).map((_, index) => {
                return <Box
                    key={index}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: '10px'
                    }}
                >
                    <Skeleton
                        variant="rounded"
                        width={135}
                        height={135 * 1.414}
                    />
                </Box>;
            })}
        </Box>;
    }

    if (!props.data.length) {
        return <Typography color={theme.palette.text.primary}>Nenhum resultado encontrado.</Typography>;
    }


    const totalizadores = props.data
        .flatMap(s => s.itens)
        .reduce(
            (acc, curr) => {
                return {
                    quantidade: acc.quantidade + curr.quantidade,
                    quantidadeDevolvida: acc.quantidadeDevolvida + curr.quantidadeDevolvida,
                    saldo: acc.saldo + curr.saldo,
                    valorTotalLiquido: acc.valorTotalLiquido + curr.valorTotalLiquido,
                };
            },
            {
                quantidade: 0,
                quantidadeDevolvida: 0,
                saldo: 0,
                valorTotalLiquido: 0,
            }
        );

    return <Box
        sx={{
            padding: 0,
            margin: 0,
            position: 'relative',
        }}>
        <ToggleButtonGroup
            sx={{
                position: 'absolute',
                right: 0,
                top: props.toogleButtonPropsTop ?? '-70px'
            }}
            value={localState.viewMode}
            exclusive
            color='primary'
            size="small"
            onChange={(_, value) => { if (value) setLocalState(prev => ({ ...prev, viewMode: value })); }}
        >       <ToggleButton value={ViewMode.GRID} >
                <Icon className='fa-solid fa-border-all' />
            </ToggleButton>

            <ToggleButton value={ViewMode.LIST} >
                <Icon className='fa-solid fa-list' />
            </ToggleButton>
        </ToggleButtonGroup>

        {
            localState.viewMode === ViewMode.GRID
                ? <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: '45px',
                }}>
                    {props.data.map((n, index) => {
                        return <MiniNota
                            data={n as FlowConsignadoVisualizarNotaBaseDTO}
                            index={index}
                            onClick={() => props.onClickMiniNota?.(n.itens)}
                            onDelete={props.onDeleteNota}
                            onCancelar={item => {
                                setLocalState(prev => ({
                                    ...prev,
                                    shouldShowCancelarNotaDialog: true,
                                    selectedItem: item,
                                }));
                            }}
                            onImprimir={props.onImprimir}
                            onImprimirDanfe={props.onImprimirDanfe}
                        />;
                    })}
                </Box>

                : <Table size='small'>
                    <TableHead>
                        <TableRow>
                            {columns.map(c => <TableCell key={c.id}>
                                <Typography align={c.textAlign} fontWeight={'bold'}>
                                    {c.header}
                                </Typography>
                            </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.data.map((item, index) => [
                            <TableRow key={item.chaveAcesso + index}>
                                {columns.map(c => <TableCell key={c.id}>
                                    <Typography align={c.textAlign}>
                                        {c.convert?.(item as any, { column: c }) ?? resolveWrapper(item, c.key)}
                                    </Typography>
                                </TableCell>
                                )}
                            </TableRow>,

                            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                                <TableCell style={{ padding: 0 }} colSpan={999}>
                                    <Collapse in={!!localState.selectedItens[item.chaveAcesso]} timeout="auto" unmountOnExit>
                                        <VisualizadorItens itens={item.itens} />
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        ])}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell
                                colSpan={999}
                                sx={{
                                    margin: 0,
                                    padding: 0,
                                    border: 'none'
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}>
                                    <List
                                        sx={{
                                            padding: '10px',
                                            paddingBottom: 0,
                                        }}
                                    >
                                        <ListItem sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '15px',
                                            padding: 0,
                                        }}>
                                            <Typography fontWeight={600}>Quantidade itens:</Typography>
                                            <Typography>{totalizadores.quantidade}</Typography>
                                        </ListItem>

                                        <ListItem sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '15px',
                                            padding: 0,
                                        }}>
                                            <Typography fontWeight={600}>Quantidade devolvida:</Typography>
                                            <Typography>{totalizadores.quantidadeDevolvida}</Typography>
                                        </ListItem>

                                        <ListItem sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '15px',
                                            padding: 0,
                                        }}>
                                            <Typography fontWeight={600}>Saldo:</Typography>
                                            <Typography>{totalizadores.saldo}</Typography>
                                        </ListItem>

                                        <ListItem sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '15px',
                                            padding: 0,
                                        }}>
                                            <Typography fontWeight={600}>Valor total liquido:</Typography>
                                            <Typography>{currencyOf(totalizadores.valorTotalLiquido)}</Typography>
                                        </ListItem>
                                    </List>
                                </Box>

                            </TableCell>

                        </TableRow>
                    </TableFooter>
                </Table>
        }

        {!!localState.selectedItem && <CancelaDFeDialog
            open={localState.shouldShowCancelarNotaDialog}
            onClose={() => {
                props.onReload?.();
                setLocalState(prev => ({ ...prev, shouldShowCancelarNotaDialog: false }));
            }}
            item={{
                serie: localState.selectedItem.serie,
                numeroNotaFiscal: localState.selectedItem.numero,
                chaveAcesso: localState.selectedItem.chaveAcesso,
                modelo: 'NF-e',
                tipoNotaFiscal: localState.selectedItem.tipo === EntradaSaida.SAIDA ? 'VENDA' : 'DEVOLUCAO_VENDA'
            }}
        />
        }
    </Box>;
};

export default VisualizadorNotas;

export const VisualizadorItens = (props: { itens: FlowConsignadoItemVisualizarDTO[]; }) => {

    const isColumnVisible = () => props.itens[0].tipo === EntradaSaida.SAIDA;

    const _columnsItens: ColumnData<FlowConsignadoItemVisualizarDTO>[] = [
        {
            header: 'SKU',
            id: 'sku',
            key: 'skuDescritivo',
            type: 'string',
            width: 10,
        },

        {
            header: 'Quantidade',
            id: 'quantidade',
            key: 'quantidade',
            type: 'string',
            textAlign: 'center',
            width: 10,
        },

        {
            header: 'Quantidade devolvida',
            id: 'quantidadeDevolvida',
            key: 'quantidadeDevolvida',
            type: 'string',
            textAlign: 'center',
            width: 10,
            visible: isColumnVisible
        },

        {
            header: 'Saldo',
            id: 'saldo',
            key: 'saldo',
            type: 'string',
            textAlign: 'center',
            width: 10,
            visible: isColumnVisible

        },

        {
            header: '% desconto',
            id: 'percentualDesconto',
            key: 'percentualDesconto',
            type: 'string',
            textAlign: 'center',
            width: 10,
            convert: item => {
                return <>{item.percentualDesconto}%</>;
            },
        },

        {
            header: 'Valor de desconto',
            id: 'valorDesconto',
            key: 'valorDesconto',
            type: 'string',
            textAlign: 'center',
            width: 10,
            convert: item => {
                return <>{currencyOf(item.valorDesconto)}</>;
            },
        },

        {
            header: 'Valor unitário',
            id: 'valorUnitario',
            key: 'valorUnitario',
            type: 'string',
            textAlign: 'center',
            width: 10,
            convert: item => {
                return <>{currencyOf(item.valorUnitario)}</>;
            },
        },

        {
            header: 'Valor Total',
            id: 'valorTotalLiquido',
            key: 'valorTotalLiquido',
            type: 'string',
            textAlign: 'center',
            width: 10,
            convert: item => {
                return <>{currencyOf(item.valorTotalLiquido)}</>;
            },
        },
    ];

    const getColumns = () => _columnsItens.filter(s => !s.visible || s.visible());

    const totalizadores = props.itens.reduce(
        (acc, curr) => {
            return {
                skuDescritivo: acc.skuDescritivo,
                quantidade: acc.quantidade + curr.quantidade,
                quantidadeDevolvida: acc.quantidadeDevolvida + curr.quantidadeDevolvida,
                saldo: acc.saldo + curr.saldo,
                valorTotalLiquido: acc.valorTotalLiquido + curr.valorTotalLiquido,
            };
        },
        {
            skuDescritivo: 'Total',
            quantidade: 0,
            quantidadeDevolvida: 0,
            saldo: 0,
            valorTotalLiquido: 0,
        }
    );

    return <Table size='small'>
        <TableHead>
            <TableRow>
                {getColumns().map(c => <TableCell key={c.id}>
                    <Typography align={c.textAlign} fontWeight={'bold'}>
                        {c.header}
                    </Typography>
                </TableCell>
                )}
            </TableRow>
        </TableHead>

        <TableBody>
            {props.itens.map(item => {
                return <TableRow key={item.uuid}>
                    {getColumns().map(c => <TableCell key={c.id}>
                        <Typography align={c.textAlign}>
                            {c.convert?.(item, { column: c }) ?? resolveWrapper(item, c.key)}
                        </Typography>
                    </TableCell>
                    )}
                </TableRow>;
            })}

            <TableRow>
                {getColumns().map(c => {
                    let value = (totalizadores as any)[c.key];
                    if (!!value && !!c.convert) {
                        value = c.convert(totalizadores as any, { column: c });
                    }
                    return <TableCell key={c.id}>
                        <Typography align={c.textAlign} fontWeight={600} color={theme.palette.text.primary}>
                            {value}
                        </Typography>
                    </TableCell>;
                })}
            </TableRow>
        </TableBody>
    </Table>;
};

const ImprimirButton = ({ item, onImprimir, onImprimirDanfe, onDeleteNota, onCancelar }: {
    item: FlowConsignadoVisualizarNotaBaseDTO;
    onImprimir?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onImprimirDanfe?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onDeleteNota?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
    onCancelar?: (item: FlowConsignadoVisualizarNotaBaseDTO) => void;
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleCloseMenuImprimir = () => {
        setAnchorEl(null);
    };

    const shouldShowImprimirDanfe = item.situacao === StatusNotaFiscal.AUTORIZADA && !!onImprimirDanfe;
    const shouldShowImprimirRomaneio = item.tipo === EntradaSaida.SAIDA && !!onImprimir;
    const shouldShowDelete = item.situacao === StatusNotaFiscal.REJEITADA && !!onDeleteNota;
    const shouldShowCancelar = item.situacao === StatusNotaFiscal.AUTORIZADA && !!onCancelar;

    return <>
        {(shouldShowImprimirDanfe || shouldShowImprimirRomaneio) && <>
            <Tooltip
                title={'Imprimir'}
                arrow
            >
                <IconButton
                    size="small"
                    onClick={e => setAnchorEl(e.currentTarget)}
                >
                    <Print sx={{ color: theme.palette.success.main }} />
                </IconButton>
            </Tooltip>

            <Menu key="menu_row"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenuImprimir}
            >
                <MenuList>
                    {shouldShowImprimirDanfe &&
                        <MenuItem
                            onClick={() => {
                                handleCloseMenuImprimir();
                                onImprimirDanfe?.(item);
                            }}
                        >
                            <ListItemText>Danfe</ListItemText>
                        </MenuItem>
                    }

                    {shouldShowImprimirRomaneio &&
                        <MenuItem
                            onClick={() => {
                                handleCloseMenuImprimir();
                                onImprimir?.(item);
                            }}
                        >
                            <ListItemText>Romaneio</ListItemText>
                        </MenuItem>
                    }
                </MenuList>
            </Menu>
        </>
        }

        {shouldShowDelete && <Tooltip
            title={'Excluir'}
            arrow
        >
            <IconButton
                size="small"
                onClick={() => onDeleteNota?.(item)}
            >
                <Delete sx={{ color: theme.palette.error.main }} />
            </IconButton>
        </Tooltip>
        }

        {shouldShowCancelar && <Tooltip
            title={'Cancelar'}
            arrow
        >
            <IconButton
                size="small"
                onClick={() => onCancelar?.(item)}
            >
                <Block sx={{ color: theme.palette.error.main }} />
            </IconButton>
        </Tooltip>
        }
    </>;
};
